//TODO : Refactor after ai
import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PagesRoutes } from "@app/router";
import { useAuthStore } from "@app/store";
import { useLoginApi } from "@services/auth";
import { Button } from "@shared/components";
import { env } from "@shared/config";
import { DEMO_LOGIN } from "@shared/demo-data/viacid";
import { useTranslation } from "@shared/locales";
import { validateEmail, validatePassword } from "../utils";
import type { FieldErrors } from "../utils";
import { AuthShell } from "./AuthShell";
import { FormField } from "./FormField";

type LoginField = "email" | "password";

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setSession = useAuthStore((state) => state.setSession);

  // Im Mock-Modus mit dem Demo-Zugang vorausgefüllt, damit die Vorführung direkt startet.
  const [email, setEmail] = useState<string>(env.api.useMockData ? DEMO_LOGIN.email : "");
  const [password, setPassword] = useState<string>(env.api.useMockData ? DEMO_LOGIN.passwort : "");
  const [errors, setErrors] = useState<FieldErrors<LoginField>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const login = useLoginApi({
    // Failures belong in the card banner, not the global toast/error-page
    // pipeline — the user stays on the form with their input intact.
    withoutToastMessage: true,
    navigateOnError: false,
    // Adopting the session is one write, so `isAuthenticated` never flips
    // before the token lands (which would let PublicOnlyRoute bounce the user
    // mid-login).
    onSuccess: (session) => {
      setSession(session);
      navigate(PagesRoutes.home.index);
    },
    onError: (error) =>
      setServerError(error?.message ?? t("auth.errors.invalidCredentials")),
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setServerError(null);

    const nextErrors: FieldErrors<LoginField> = {
      email: validateEmail(email),
      password: validatePassword(password),
    };
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    login.mutate({ email, password });
  };

  return (
    <AuthShell
      title={t("auth.login.title")}
      description={t("auth.login.description")}
      serverError={serverError}
      footer={
        <>
          {t("auth.login.noAccount")}{" "}
          <Link
            to={PagesRoutes.auth.register}
            className="font-medium text-foreground hover:underline"
          >
            {t("auth.login.createAccount")}
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <FormField
          id="login-email"
          type="email"
          label={t("auth.fields.email")}
          placeholder={t("auth.fields.emailPlaceholder")}
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={errors.email && t(errors.email)}
        />
        <FormField
          id="login-password"
          type="password"
          label={t("auth.fields.password")}
          placeholder="••••••••"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={errors.password && t(errors.password)}
        />

        <div className="flex items-center justify-end">
          <Link
            to={PagesRoutes.auth["forgot-password"]}
            className="text-sm font-medium text-foreground hover:underline"
          >
            {t("auth.login.forgotPassword")}
          </Link>
        </div>

        <Button type="submit" className="w-full" disabled={login.isPending}>
          {login.isPending
            ? t("auth.login.submitting")
            : t("auth.login.submit")}
        </Button>
      </form>
    </AuthShell>
  );
}
